import { useState, useMemo, useRef, createRef, RefObject } from 'react'
import {
  Box,
  Typography,
  TextField,
  FormControl,
  MenuItem,
} from '@mui/material'
import { Modal, Button } from 'Common/Supplyvue-ui'
import {
  GridColDef,
  GridRowSelectionModel,
  GridValueOptionsParams,
} from '@mui/x-data-grid-premium'

// Define the sx props for styled components
const modalBodySx = {
  display: 'flex',
  flexDirection: 'column',
  padding: '20px',
  minWidth: '600px',
  minHeight: '200px',
  maxHeight: '80vh',
  overflowY: 'auto',
}

const footerSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
}

const titleSx = {
  marginBottom: '20px',
}

const selectedRowsInfoSx = {
  marginBottom: '10px',
}

const formFieldContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '16px',
  width: '100%',
}

const formFieldSx = {
  marginBottom: '0px',
}

interface BulkUpdateValues {
  [key: string]: string | number | null
}

export type ExtendedGridColDef = GridColDef & {
  /** A plain array is the common case and is what this modal renders as a
   *  dropdown. A function is allowed because a `singleSelect` column's options
   *  can depend on the row (e.g. the carry-over kitchen/line pickers, which
   *  offer only routes valid for that product) — such a column has no single
   *  option list to bulk-apply, so it falls back to a free-text field here. */
  valueOptions?:
    | string[]
    | number[]
    | ((params: GridValueOptionsParams) => string[] | number[])
}

interface BulkUpdateModalProps<T = Record<string, unknown>> {
  open: boolean
  onClose: () => void
  selectedRows: GridRowSelectionModel
  columns: ExtendedGridColDef[]
  onApply: (values: BulkUpdateValues) => void
}

export const BulkUpdateModal = <T extends Record<string, unknown>>({
  open,
  onClose,
  selectedRows,
  columns,
  onApply,
}: BulkUpdateModalProps<T>) => {
  // Internal state that won't affect parent rendering
  const [internalValues, setInternalValues] = useState<BulkUpdateValues>({})
  // Create refs for text fields for keyboard navigation
  const textFieldRefs = useRef<RefObject<HTMLInputElement>[]>([])

  // Get only editable columns
  const editableColumns = useMemo(() => {
    return columns.filter((column) => {
      return (
        column.editable === true &&
        column.field !== 'id' &&
        column.field !== '__check__' &&
        column.field !== 'actions' &&
        column.field
      )
    })
  }, [columns])

  // Initialize refs when columns change
  useMemo(() => {
    textFieldRefs.current = editableColumns.map(() =>
      createRef<HTMLInputElement>()
    )
  }, [editableColumns])

  // Reset values when modal opens and focus first field
  useMemo(() => {
    if (open) {
      setInternalValues({})
      // Focus the first text field after a short delay to ensure the modal is fully rendered
      setTimeout(() => {
        if (
          textFieldRefs.current.length > 0 &&
          textFieldRefs.current[0].current
        ) {
          textFieldRefs.current[0].current.focus()
        }
      }, 100)
    }
  }, [open])

  // Handle input change - contained within this component
  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = event.target
    setInternalValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Handle apply - only communicate with parent when apply is clicked
  const handleApplyClick = () => {
    if (Object.keys(internalValues).length === 0) {
      onClose()
      return
    }

    // Coerce values to the correct type based on column definitions.
    // TextField always yields strings, but number columns must be sent as
    // numbers or the backend parquet write fails with a mixed-type column.
    const coercedValues: BulkUpdateValues = {}
    for (const [field, value] of Object.entries(internalValues)) {
      if (value === null || value === '') {
        coercedValues[field] = value
        continue
      }
      const column = editableColumns.find((c) => c.field === field)
      if (column?.type === 'number' && typeof value === 'string') {
        const parsed = Number(value)
        coercedValues[field] = Number.isNaN(parsed) ? value : parsed
      } else {
        coercedValues[field] = value
      }
    }

    onApply(coercedValues)
    onClose()
  }

  // Handle key press for navigation and submission
  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault()

      // If there is a next field, focus on it
      if (index < textFieldRefs.current.length - 1) {
        const nextTextField = textFieldRefs.current[index + 1].current
        if (nextTextField) {
          nextTextField.focus()
        }
        return
      }
      // If this is the last field and we have values, submit the form
      if (Object.keys(internalValues).length > 0) {
        handleApplyClick()
      }
    }
  }

  return (
    <Modal
      open={open}
      handleClose={onClose}
      header="Bulk update"
      body={
        <Box sx={modalBodySx}>
          <Typography variant="h5" sx={titleSx}>
            Bulk update Selected Rows
          </Typography>
          <Typography variant="body1" sx={selectedRowsInfoSx}>
            You have selected {selectedRows.ids.size} row(s) to update.
          </Typography>

          {editableColumns.length === 0 ? (
            <Typography variant="body2">
              No editable columns available for bulk update.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ marginBottom: '16px' }}>
                Please enter the values you want to apply to all selected rows.
                Leave a field blank if you don&#39;t want to update that field.
              </Typography>

              <Box sx={formFieldContainerSx}>
                {editableColumns.map((column, index) => (
                  <FormControl key={column.field} sx={formFieldSx}>
                    <TextField
                      label={column.headerName}
                      name={column.field}
                      variant="outlined"
                      value={internalValues[column.field] ?? ''}
                      onChange={handleInputChange}
                      onKeyPress={(e) => handleKeyPress(e, index)}
                      inputRef={textFieldRefs.current[index]}
                      select={Array.isArray(column.valueOptions)}
                    >
                      {Array.isArray(column.valueOptions) &&
                        (column.valueOptions as Array<string | number>).map(
                          (option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          )
                        )}
                    </TextField>
                  </FormControl>
                ))}
              </Box>
            </>
          )}
        </Box>
      }
      footer={
        <Box sx={footerSx}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyClick}
            disabled={
              editableColumns.length === 0 ||
              Object.keys(internalValues).length === 0
            }
          >
            Apply
          </Button>
        </Box>
      }
    />
  )
}
